import produce from 'immer';
import { useEffect, useReducer } from 'react';

export const preloadImage = (
  src: string,
  priority?: 'high' | 'low' | 'auto'
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    // @ts-ignore
    img.fetchPriority = priority || 'auto';
    img.onload = () => {
      resolve(src);
    };
    img.onerror = () => {
      reject(`Image not found URL: ${src}`);
    };
  });
};

type ImageState = {
  loadedImage: string | undefined;
  loading: boolean;
  error: boolean;
};

const initialState: ImageState = {
  loadedImage: undefined,
  loading: false,
  error: false,
};

enum ACTION_TYPES {
  LOADING = 0,
  LOADED = 1,
  ERROR = 2,
}

type ImageAction =
  | { type: ACTION_TYPES.LOADING }
  | {
      type: ACTION_TYPES.LOADED;
      payload: { loadedImage: string };
    }
  | { type: ACTION_TYPES.ERROR };

function reducer(state: ImageState, action: ImageAction): ImageState {
  return produce(state, (draft) => {
    switch (action.type) {
      case ACTION_TYPES.LOADING:
        draft.loadedImage = undefined;
        draft.loading = true;
        draft.error = false;
        break;
      case ACTION_TYPES.LOADED:
        draft.loadedImage = action.payload.loadedImage;
        draft.loading = false;
        draft.error = false;
        break;
      case ACTION_TYPES.ERROR:
        draft.loadedImage = undefined;
        draft.loading = false;
        draft.error = true;
        break;
    }
  });
}

export function useLoadImage(src: string) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const action = async () => {
      dispatch({ type: ACTION_TYPES.LOADING });

      try {
        const loadedImage = await preloadImage(src);
        dispatch({ type: ACTION_TYPES.LOADED, payload: { loadedImage } });
      } catch {
        dispatch({ type: ACTION_TYPES.ERROR });
      }
    };

    action();
  }, [src]);

  return state;
}
